import React, { useState } from "react";
import { DownOutlined, EditOutlined } from "@ant-design/icons";
import { HistoryFieldChange } from "../../../redux/interfaces/folder.types.ts";

interface Props {
  changes: HistoryFieldChange[];
}

const EMPTY_PLACEHOLDER = "Vacío";

// Backend values are mostly strings, but may be number/null/array. Render a
// neutral placeholder for null/empty and join array values for readability.
const formatValue = (value: HistoryFieldChange["old_value"]): string => {
  if (value === null || value === undefined) return EMPTY_PLACEHOLDER;
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => String(item))
      .filter((item) => item.trim() !== "")
      .join(", ");
    return joined === "" ? EMPTY_PLACEHOLDER : joined;
  }
  const text = String(value);
  return text.trim() === "" ? EMPTY_PLACEHOLDER : text;
};

const isEmptyValue = (value: HistoryFieldChange["old_value"]): boolean =>
  formatValue(value) === EMPTY_PLACEHOLDER;

export const HistoryFieldChanges: React.FC<Props> = ({ changes }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!changes.length) return null;

  return (
    <div className="mt-2 mb-1 rounded-lg border border-slate-200 overflow-hidden">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-slate-50 ${
          isOpen ? "bg-slate-50" : "bg-white"
        }`}
      >
        <span className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-50 text-blue-600">
            <EditOutlined style={{ fontSize: 12 }} />
          </span>
          <span className="text-sm font-medium text-slate-700">
            Campos actualizados
          </span>
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-slate-100 px-1.5 text-xs font-semibold text-slate-500">
            {changes.length}
          </span>
        </span>
        <DownOutlined
          style={{ fontSize: 11 }}
          className={`text-slate-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen ? (
        <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50/60 p-3">
          {changes.map((change) => (
            <div
              key={change.key}
              className="rounded-md border border-slate-200 bg-white px-3 py-2"
            >
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
                {change.label || change.key}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`max-w-full truncate rounded px-2 py-0.5 text-sm ${
                    isEmptyValue(change.old_value)
                      ? "italic text-slate-400 bg-slate-50"
                      : "text-slate-500 line-through bg-slate-100"
                  }`}
                  title={formatValue(change.old_value)}
                >
                  {formatValue(change.old_value)}
                </span>
                <span className="text-slate-300">→</span>
                <span
                  className={`max-w-full truncate rounded px-2 py-0.5 text-sm font-medium ${
                    isEmptyValue(change.new_value)
                      ? "italic text-slate-400 bg-slate-50"
                      : "text-blue-700 bg-blue-50"
                  }`}
                  title={formatValue(change.new_value)}
                >
                  {formatValue(change.new_value)}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
